/**
 * The dashboard's handle on `@modulus/api`.
 *
 * Everything that used to live here — the two headers, the single in-flight
 * refresh, the `ApiError` status semantics — moved into the shared package so the
 * student app and the admin panel get the same behaviour rather than three
 * near-identical copies of it. This file is the adapter, and it keeps the
 * `apiRequest(path, options)` shape the pages already call.
 *
 * The base URL stays same-origin. In production the BFF Worker is bound to `/api/*`
 * on this subdomain; in development Vite proxies the same path to `wrangler dev`.
 * Neither is visible from here, which is the point.
 */

import {
  ApiClient,
  ApiError,
  SessionExpiredError,
  createApi,
  localStorageSession,
  type RequestOptions,
  type StoredSession,
} from '@modulus/api'

const store = localStorageSession()

export const api = createApi({ store })
export const client: ApiClient = api.client

export { ApiError, SessionExpiredError }
export type { StoredSession }

export const loadSession = (): StoredSession | null => store.load()
export const saveSession = (session: StoredSession): void => store.save(session)
export const clearSession = (): void => store.clear()

/** Lets the session context tear down its state when a refresh finally fails. */
export function setSessionLostHandler(handler: (() => void) | null): void {
  client.onSessionLost(handler)
}

/** The call every page makes. `path` is relative to `/api/v1`. */
export function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return client.request<T>(path, options)
}

export function login(email: string, password: string): Promise<StoredSession> {
  return api.auth.login(email, password)
}

export function logout(): Promise<void> {
  return api.auth.logout()
}
